import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

interface SidebarProps {
    collapsed: boolean;
    mobileShow: boolean;
    setMobileShow: (show: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, mobileShow, setMobileShow }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, token, logout } = useAuth();
    const currentPath = location.pathname;
    
    const scrollRef = React.useRef<HTMLDivElement>(null);

    const [projectMenuOpen, setProjectMenuOpen] = React.useState(
        currentPath.includes('/admin-projects') || currentPath.includes('/admin-add-project')
    );

    const [inquiryMenuOpen, setInquiryMenuOpen] = React.useState(
        currentPath.includes('/admin-project-inquiries') || 
        currentPath.includes('/admin-locality-inquiries') || 
        currentPath.includes('/admin-common-inquiries')
    );

    // Badge counts fetched from API
    const [badgeCounts, setBadgeCounts] = React.useState<Record<string, number>>({});

    React.useEffect(() => {
        if (!token) return;
        api.adminGetStats(token).then((data: any) => {
            if (data && !data.message) {
                setBadgeCounts({
                    '/admin-leads': data.newLeads || 0,
                    '/admin-bookings': data.pendingBookings ?? data.totalBookings ?? 0,
                    '/admin-package-subscriptions': data.pendingSubscriptions || 0,
                    '/admin-package-inquiries': data.pendingPackageInquiries || 0,
                    '/admin-project-inquiries': data.pendingProjectInquiries || 0,
                    '/admin-locality-inquiries': data.pendingLocalityInquiries || 0,
                    '/admin-common-inquiries': data.pendingCommonInquiries || 0,
                });
            } else if (data?.message?.includes('Not authorized') || data?.message?.includes('Session expired') || data?.message?.includes('User not found')) {
                logout();
            }
        }).catch(console.error);
    }, [token, logout]);

    const handleScroll = () => {
        if (scrollRef.current) {
            const { scrollTop } = scrollRef.current;
            localStorage.setItem('admin-sidebar-scroll', scrollTop.toString());
        }
    };

    React.useEffect(() => {
        const savedScroll = localStorage.getItem('admin-sidebar-scroll');
        if (savedScroll && scrollRef.current) {
            scrollRef.current.scrollTop = parseInt(savedScroll);
        }
        handleScroll();
        window.addEventListener('resize', handleScroll);
        return () => window.removeEventListener('resize', handleScroll);
    }, []);


    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    // badge: string = label for static badges, number key for dynamic
    const menuGroups = [
        {
            title: 'General',
            items: [
                { label: 'Dashboard', path: '/admin-dashboard', icon: 'fa-gauge-high' },
                { label: 'Manage Users', path: '/admin-users', icon: 'fa-user-shield' },
                { label: 'Sub-Admin', path: '/admin-sub-admins', icon: 'fa-user-gear' },
                { label: 'Site Visibility', path: '/admin-site-visibility', icon: 'fa-eye-slash' },
                { label: 'Lead Settings', path: '/admin-lead-settings', icon: 'fa-user-lock' },
                { label: 'SEO Management', path: '/admin-seo', icon: 'fa-magnifying-glass-chart' },
                { label: 'Score Management', path: '/admin-score-management', icon: 'fa-chart-pie' },
                { label: 'Legal Content', path: '/admin-legal', icon: 'fa-file-shield' },
            ]
        },
        {
            title: 'Business & Inventory',
            items: [
                { label: 'Manage Packages', path: '/admin-packages', icon: 'fa-boxes-packing' },
                { label: 'Package Subscriptions', path: '/admin-package-subscriptions', icon: 'fa-file-invoice-dollar', badgeKey: '/admin-package-subscriptions', badgeColor: 'bg-emerald-500' },
                { label: 'Package Inquiries', path: '/admin-package-inquiries', icon: 'fa-envelope-open-text', badgeKey: '/admin-package-inquiries', badgeColor: 'bg-rose-500' },
                { label: 'Inventory', path: '/admin-inventory', icon: 'fa-box-archive' },
                { label: 'Developers', path: '/admin-developers', icon: 'fa-building-columns' },
                { label: 'Site Visit Bookings', path: '/admin-bookings', icon: 'fa-calendar-check', badgeKey: '/admin-bookings', badgeColor: 'bg-amber-500' },
                { label: 'CRM Leads', path: '/admin-leads', icon: 'fa-headset', badgeKey: '/admin-leads', badgeColor: 'bg-rose-500' },
            ]
        },
        {
            title: 'Lead Classified',
            items: [
                { label: 'Lead Dashboard', path: '/admin-classified-dashboard', icon: 'fa-chart-simple' },
                { label: 'Default Lead Images', path: '/admin-default-lead-images', icon: 'fa-images' },
                { label: 'Add Classified Property', path: '/admin-add-property', icon: 'fa-file-excel' },
                { label: 'Clasified Leads list', path: '/admin-saved-properties', icon: 'fa-bookmark' },
                { label: 'Lead Classified Package', path: '/admin-lead-classified-packages', icon: 'fa-cubes' },
                { label: 'Lead Package Purchases', path: '/admin-lead-subscriptions', icon: 'fa-receipt' },
            ]
        },
        {
            title: 'Geography & Trends',
            items: [
                { label: 'Cities', path: '/admin-cities', icon: 'fa-city' },
                { label: 'Localities', path: '/admin-localities', icon: 'fa-map-location-dot' },
                { label: 'Locality Highlights', path: '/admin-locality-highlights', icon: 'fa-star' },
                { label: 'Landmark Categories', path: '/admin-landmark-categories', icon: 'fa-map-pin' },
                { label: 'Price Trends', path: '/admin-price-trends', icon: 'fa-chart-line' },
            ]
        },
        {
            title: 'Content & Media',
            items: [
                { label: 'Media Folders', path: '/admin-media', icon: 'fa-folder-open' },
                { label: 'Amenities', path: '/admin-amenities', icon: 'fa-wand-magic-sparkles' },
                { label: 'Specifications', path: '/admin-specifications', icon: 'fa-list-check' },
                { label: 'Icon Library', path: '/admin-icons', icon: 'fa-icons' },
                { label: 'Podcasts', path: '/admin-podcasts', icon: 'fa-microphone-lines' },
                { label: 'Reel Management', path: '/admin-reels', icon: 'fa-clapperboard' },
                { label: 'Testimonials', path: '/admin-testimonials', icon: 'fa-quote-left' },
                { label: 'Blog Posts', path: '/admin-blogs', icon: 'fa-newspaper' },
                { label: 'Blog Comments', path: '/admin-blog-comments', icon: 'fa-comments' },
                { label: 'Hero Banners', path: '/admin-hero-banners', icon: 'fa-images' },
            ]
        },
        {
            title: 'Account',
            items: [
                { label: 'My Profile', path: '/admin-profile', icon: 'fa-id-card-clip' },
                { label: 'Security', path: '/admin-change-password', icon: 'fa-shield-halved' },
            ]
        }
    ] as const;

    const filteredMenuGroups = user?.role === 'sub-admin' 
        ? menuGroups.map(group => ({
            ...group,
            items: (group.items as readonly any[]).filter((item: any) => {
                const isAlwaysVisible = ['Dashboard', 'My Profile', 'Security'].includes(item.label);
                const hasPerm = user.permissions && Array.isArray(user.permissions) && 
                    user.permissions.some((p: string) => p.trim().toLowerCase() === item.label.trim().toLowerCase());
                return isAlwaysVisible || hasPerm;
            })
        })).filter(group => group.items.length > 0)
        : menuGroups;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: `
                .admin-sidebar::-webkit-scrollbar {
                    width: 3px;
                }
                .admin-sidebar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .admin-sidebar::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 10px;
                }
                .admin-sidebar:hover::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.2);
                }
            `}} />
            
            <aside className={`fixed md:relative inset-y-0 left-0 z-[1000] md:z-10 h-screen shrink-0 text-white transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] flex flex-col shadow-2xl overflow-hidden ${collapsed ? 'w-24' : 'w-[280px]'} ${mobileShow ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`} style={{ background: 'linear-gradient(to bottom, #05144b 0%, #4c1d95 100%)' }}>
                {/* Sidebar Header */}
                <div className={`flex items-center justify-center px-6 shrink-0 z-30 bg-transparent ${collapsed ? 'h-20' : 'h-24'}`}>
                    <div className="flex items-center gap-3">
                        <img src="/white-logo.png" alt="Logo" className="w-24 h-24 object-contain animate__animated animate__zoomIn" />
                    </div>
                </div>



                {/* Sidebar Content */}
                <div 
                    ref={scrollRef}
                    onScroll={handleScroll}
                    className={`admin-sidebar flex-1 overflow-y-auto overflow-x-hidden pt-4 pb-8 transition-all duration-500 relative scroll-smooth`}
                >
                    <nav className="space-y-6">
                        {user?.role === 'sub-admin' && (
                            <div className="px-6 py-2 mb-4 bg-white/10 rounded-xl border border-white/10">
                                <p className="text-[10px] font-bold text-white uppercase opacity-50">Debug Mode</p>
                                <p className="text-[12px] font-bold text-[#ffbb00]">Role: {user.role}</p>
                                <p className="text-[12px] font-bold text-white">Perms: {user.permissions?.length || 0}</p>
                            </div>
                        )}
                        {filteredMenuGroups.map((group, gIdx) => (
                            <div key={gIdx} className="space-y-1">
                                 {!collapsed && (
                                     <div className="mx-3 mb-2 mt-4">
                                         <div className="w-full flex items-center justify-between py-2 px-4 rounded-xl bg-[#f4ad1a] shadow-md select-none">
                                             <span className="text-[15px] font-extrabold tracking-wide text-[#05144b]">
                                                 {group.title}
                                             </span>
                                             <i className="fa-solid fa-chevron-right text-[12px] text-[#05144b]"></i>
                                         </div>
                                     </div>
                                 )}
                                
                                {group.items.map((item: any) => {
                                    const isActive = currentPath === item.path;
                                    const badgeCount = item.badgeKey ? (badgeCounts[item.badgeKey] || 0) : 0;
                                    const showBadge = badgeCount > 0;
                                    
                                    // Inject Dropdowns at specific points
                                    const renderProjectDropdown = group.title === 'General' && item.label === 'Dashboard' && (user?.role !== 'sub-admin' || user.permissions?.includes('Project Manager'));
                                    const renderInquiryDropdown = group.title === 'Business & Inventory' && (item.label === 'Manage Packages' || (user?.role === 'sub-admin' && item.label === 'Inventory' && !user.permissions?.includes('Manage Packages'))) && (user?.role !== 'sub-admin' || user.permissions?.includes('Inquiry Portal'));

                                    return (
                                        <React.Fragment key={item.path}>
                                            <Link
                                                to={item.path}
                                                className={`group flex items-center gap-3 py-2.5 px-4 transition-all duration-300 relative mx-3 rounded-xl ${isActive ? 'bg-white text-[#05144b] shadow-lg translate-x-1' : 'text-blue-100 hover:bg-white/10 hover:text-white'}`}
                                                onClick={() => setMobileShow(false)}
                                            >
                                                {/* Icon with badge dot in collapsed mode */}
                                                <div className="relative flex-shrink-0">
                                                    <i className={`fa-solid ${item.icon} text-[20px] transition-colors duration-500 ${isActive ? 'text-[#05144b]' : 'text-blue-100/70 group-hover:text-white'}`}></i>
                                                    {/* Dot badge in collapsed mode */}
                                                    {collapsed && showBadge && (
                                                        <span className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full border-2 border-[#05144b] ${item.badgeColor || 'bg-rose-500'} animate-pulse`}></span>
                                                    )}
                                                </div>

                                                {!collapsed && (
                                                    <>
                                                        <span className={`text-[15px] font-bold tracking-tight flex-1`}>
                                                            {item.label}
                                                        </span>
                                                        {/* Number badge in expanded mode */}
                                                        {showBadge && (
                                                            <span className={`ml-auto text-[10px] font-black px-1.5 py-0.5 rounded-full text-white leading-none border-2 ${
                                                                isActive ? 'border-[#05144b]/20 bg-[#05144b]' : `border-white/10 ${item.badgeColor || 'bg-rose-500'}`
                                                            } animate-pulse`}>
                                                                {badgeCount > 99 ? '99+' : badgeCount}
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </Link>

                                            {/* Projects Dropdown */}
                                            {renderProjectDropdown && (
                                                <div className={`mx-3 mt-1 rounded-xl transition-all duration-300 ${projectMenuOpen || currentPath.includes('project') ? 'bg-white/5' : ''}`}>
                                                    <button
                                                        onClick={() => setProjectMenuOpen(!projectMenuOpen)}
                                                        className={`w-full group flex items-center justify-between px-4 py-2.5 rounded-xl transition-all duration-300 ${projectMenuOpen || currentPath.includes('project') ? 'text-white' : 'text-blue-100 hover:text-white hover:bg-white/5'}`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <i className={`fa-solid fa-diagram-project text-[20px] transition-colors duration-300 ${projectMenuOpen || currentPath.includes('project') ? 'text-[#ffbb00]' : 'text-blue-100/70 group-hover:text-white'}`}></i>
                                                            {!collapsed && <span className="text-[15px] font-bold tracking-tight">Project Manager</span>}
                                                        </div>
                                                        {!collapsed && (
                                                            <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-500 ${projectMenuOpen ? 'rotate-180 text-[#ffbb00]' : 'text-blue-100/30'}`}></i>
                                                        )}
                                                    </button>
                                                    
                                                    <div className={`overflow-hidden transition-all duration-500 ease-in-out ${projectMenuOpen && !collapsed ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'}`}>
                                                        <div className="space-y-1 p-2 pt-0">
                                                            {[
                                                                { label: 'All Projects', path: '/admin-projects' },
                                                                { label: 'Project Drafts', path: '/admin-drafts' },
                                                                { label: 'Add Project', path: '/admin-add-project' },
                                                                { label: 'Media Folders', path: '/admin-media' }
                                                            ].map(sub => (
                                                                <Link
                                                                    key={sub.path}
                                                                    to={sub.path}
                                                                    className={`block text-[13px] font-bold py-2 px-4 rounded-lg transition-all ${currentPath === sub.path ? 'bg-[#ffbb00] text-[#05144b]' : 'text-blue-100/70 hover:text-white hover:bg-white/5'}`}
                                                                    onClick={() => setMobileShow(false)}
                                                                >
                                                                    {sub.label}
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Inquiry Dropdown */}
                                            {renderInquiryDropdown && (
                                                <div className={`mx-3 mt-1 rounded-xl transition-all duration-300 ${inquiryMenuOpen || currentPath.includes('inquir') ? 'bg-white/5' : ''}`}>
                                                    <button
                                                        onClick={() => setInquiryMenuOpen(!inquiryMenuOpen)}
                                                        className={`w-full group flex items-center justify-between px-4 py-2.5 rounded-xl transition-all duration-300 ${inquiryMenuOpen || currentPath.includes('inquir') ? 'text-white' : 'text-blue-100 hover:text-white hover:bg-white/5'}`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <i className={`fa-solid fa-envelope-open-text text-[20px] transition-colors duration-300 ${inquiryMenuOpen || currentPath.includes('inquir') ? 'text-[#ffbb00]' : 'text-blue-100/70 group-hover:text-white'}`}></i>
                                                            {!collapsed && <span className="text-[15px] font-bold tracking-tight">Inquiry Portal</span>}
                                                        </div>
                                                        {!collapsed && (
                                                            <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-500 ${inquiryMenuOpen ? 'rotate-180 text-[#ffbb00]' : 'text-blue-100/30'}`}></i>
                                                        )}
                                                    </button>
                                                    
                                                    <div className={`overflow-hidden transition-all duration-500 ease-in-out ${inquiryMenuOpen && !collapsed ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'}`}>
                                                        <div className="space-y-1 p-2 pt-0">
                                                            {[
                                                                { label: 'Project Inq', path: '/admin-project-inquiries' },
                                                                { label: 'Locality Inq', path: '/admin-locality-inquiries' },
                                                                { label: 'Common Inq', path: '/admin-common-inquiries' }
                                                            ].map(sub => (
                                                                <Link
                                                                    key={sub.path}
                                                                    to={sub.path}
                                                                    className={`block text-[13px] font-bold py-2 px-4 rounded-lg transition-all ${currentPath === sub.path ? 'bg-[#ffbb00] text-[#05144b]' : 'text-blue-100/70 hover:text-white hover:bg-white/5'}`}
                                                                    onClick={() => setMobileShow(false)}
                                                                >
                                                                    {sub.label}
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        ))}
                    </nav>
                </div>


            </aside>
        </>
    );
};

export default Sidebar;
